import * as angular from 'angular';
import * as hippoConstFactory from 'common/hippoConst.js';
import * as _ from 'lodash';
import { isTodayBetween20And30DaysAfterDate, isTodayWithinFiveDaysBeforeDate } from 'common/source/utilities/dates';
import { formatAsDollarsWithoutZeroCentsOrEmptyString } from 'common/source/utilities/strings';
import { getPaymentsSref, getPaymentsSrefWithPayNow } from 'components/navigation/shared/states';
import { getPaymentMethod } from 'common/source/utilities/policyInfoHelper';

export const paymentMethodDisplayName = {
    escrow: 'mortgage bill',
    cc: 'credit card',
    ach: 'bank account (ACH)'
};

export const paymentOptions = {
    nextUnpaid: 'next_unpaid',
    remaining: 'remaining',
    lastFailedPayment: 'last_failed_payment'
};

const HIPPO_CONSTS = hippoConstFactory();

const SUPPORT_NUMBER = HIPPO_CONSTS.HIPPO_SUPPORT_PHONE_NUMBER;

export enum CUSTOMER_PAYMENT_STATUS {
    PENDING = 'pending',
    PAID = 'paid',
    DUE = 'due',
    NOT_RECEIVED = 'not_received',
    NOT_RECEIVED_POLICY_CANCELLED = 'not_received_policy_cancelled',
    FAILED = 'failed',
    OUTSTANDING = 'outstanding',
    CANCELLED = 'cancelled',
    DELAYED = 'delayed',
    CC_PAYMENTS_REQUIRED = 'cc_payments_required'
}

export enum DERIVED_PAYMENT_STATUS {
    PAID = 'paid',
    PAYMENT_PENDING = 'payment_pending',
    CANCELLED_WITH_BALANCE = 'cancelled_with_balance',
    OUTSTANDING_BALANCE = 'outstanding_balance',
    PAYMENT_FAILED = 'payment_failed',
    UPCOMING_PAYMENT = 'upcoming_payment',
    PAYMENT_REQUIRED = 'payment_required',
    PENDING_20_AND_30_DAYS_AFTER_EFFECTIVE_DATE = 'pending_20_and_30_days_after_effective_date',
    PENDING_CANCELLATION_NON_PAYMENT = 'pending_cancellation_non_payment',
    POLICY_CANCELLED_NON_PAYMENT = 'policy_cancelled_non_payment',
    PENDING_CANCELLATION_OTHER = 'pending_cancellation_other',
    POLICY_CANCELLED_OTHER = 'policy_cancelled_other',
    DELAYED = 'delayed'
}

export const derivedStatusDisplayLabel = {
    paid: 'Paid',
    payment_pending: 'Payment Pending',
    cancelled_with_balance: 'Cancelled With Balance',
    outstanding_balance: 'Outstanding Balance',
    payment_failed: 'Payment Failed',
    upcoming_payment: 'Upcoming Payment',
    payment_required: 'Payment Required',
    pending_20_and_30_days_after_effective_date: 'Payment Pending',
    pending_cancellation_non_payment: 'Pending Cancellation',
    policy_cancelled_non_payment: 'Policy Cancelled',
    pending_cancellation_other: 'Pending Cancellation',
    policy_cancelled_other: 'Policy Cancelled',
    delayed: 'Delayed'
};

export enum CUSTOMER_PAYMENT_METHOD {
    ESCROW = 'escrow',
    CC = 'cc',
    ACH = 'ach'
}

const isBetween20And30DaysAfterEffectiveDate = (policyInfo: CustomerPolicyInfo): boolean => {
    if (policyInfo.effective_date) {
        return isTodayBetween20And30DaysAfterDate(policyInfo.effective_date);
    }
    return false;
};

const isWithinFiveDaysBeforeDate = (policyInfo: CustomerPolicyInfo): boolean => {
    if (policyInfo.next_unpaid_payment_due_date) {
        return isTodayWithinFiveDaysBeforeDate(policyInfo.next_unpaid_payment_due_date);
    }
    return false;
};

// tslint:disable-next-line
export const getDerivedPaymentStatus = (policyInfo: CustomerPolicyInfo): DERIVED_PAYMENT_STATUS | undefined => {
    const status = policyInfo.payment_status;
    const paymentMethod = getPaymentMethod(policyInfo);
    if (policyInfo.pending_terminate) {
        if (paymentMethod === CUSTOMER_PAYMENT_METHOD.ESCROW) {
            if (status === CUSTOMER_PAYMENT_STATUS.NOT_RECEIVED) {
                return DERIVED_PAYMENT_STATUS.PENDING_CANCELLATION_NON_PAYMENT;
            } else if (status !== CUSTOMER_PAYMENT_STATUS.FAILED) {
                return DERIVED_PAYMENT_STATUS.PENDING_CANCELLATION_OTHER;
            }
        } else {
            if (status === CUSTOMER_PAYMENT_STATUS.FAILED) {
                return DERIVED_PAYMENT_STATUS.PENDING_CANCELLATION_NON_PAYMENT;
            } else {
                return DERIVED_PAYMENT_STATUS.PENDING_CANCELLATION_OTHER;
            }
        }
    }

    switch (status) {
        case CUSTOMER_PAYMENT_STATUS.PAID:
            if (
                (paymentMethod === CUSTOMER_PAYMENT_METHOD.CC || paymentMethod === CUSTOMER_PAYMENT_METHOD.ACH) &&
                isWithinFiveDaysBeforeDate(policyInfo)
            ) {
                return DERIVED_PAYMENT_STATUS.UPCOMING_PAYMENT;
            }
            return DERIVED_PAYMENT_STATUS.PAID;
        case CUSTOMER_PAYMENT_STATUS.PENDING:
            if (
                paymentMethod === CUSTOMER_PAYMENT_METHOD.ESCROW &&
                isBetween20And30DaysAfterEffectiveDate(policyInfo)
            ) {
                return DERIVED_PAYMENT_STATUS.PENDING_20_AND_30_DAYS_AFTER_EFFECTIVE_DATE;
            }
            return DERIVED_PAYMENT_STATUS.PAYMENT_PENDING;
        case CUSTOMER_PAYMENT_STATUS.NOT_RECEIVED_POLICY_CANCELLED:
            return DERIVED_PAYMENT_STATUS.POLICY_CANCELLED_NON_PAYMENT;
        case CUSTOMER_PAYMENT_STATUS.CANCELLED:
            return DERIVED_PAYMENT_STATUS.POLICY_CANCELLED_OTHER;
        case CUSTOMER_PAYMENT_STATUS.OUTSTANDING:
            if (paymentMethod === CUSTOMER_PAYMENT_METHOD.ESCROW) {
                return DERIVED_PAYMENT_STATUS.CANCELLED_WITH_BALANCE;
            } else {
                return DERIVED_PAYMENT_STATUS.OUTSTANDING_BALANCE;
            }
        case CUSTOMER_PAYMENT_STATUS.DUE:
            if (paymentMethod === CUSTOMER_PAYMENT_METHOD.ESCROW) {
                return DERIVED_PAYMENT_STATUS.OUTSTANDING_BALANCE;
            }
            break;
        case CUSTOMER_PAYMENT_STATUS.FAILED:
            if (paymentMethod === CUSTOMER_PAYMENT_METHOD.CC) {
                return DERIVED_PAYMENT_STATUS.PAYMENT_FAILED;
            }
            break;
        case CUSTOMER_PAYMENT_STATUS.CC_PAYMENTS_REQUIRED:
            if (paymentMethod === CUSTOMER_PAYMENT_METHOD.CC) {
                return DERIVED_PAYMENT_STATUS.PAYMENT_REQUIRED;
            }
            break;
        case CUSTOMER_PAYMENT_STATUS.NOT_RECEIVED:
            if (paymentMethod === CUSTOMER_PAYMENT_METHOD.ACH) {
                return DERIVED_PAYMENT_STATUS.PAYMENT_REQUIRED;
            }
            break;
    }
};

interface CallToActionMapping {
    copy: string | ((policyInfo: CustomerPolicyInfo) => string);
    header?: string;
    ctaSref?: (policyIndex: RoutePolicyIndex) => string;
}

export const getCallToActionMapping = (policyInfo: CustomerPolicyInfo): CallToActionMapping | undefined => {
    const status = getDerivedPaymentStatus(policyInfo);
    const paymentMethod = getPaymentMethod(policyInfo);
    if (status) {
        return _.get(paymentStatusToActionTextMap, [status, paymentMethod]);
    }
};

const outstandingBalanceCopy = (policyInfo: CustomerPolicyInfo) =>
    `Based on the cancellation date of your policy, there is an outstanding balance of ` +
    `<strong>${formatAsDollarsWithoutZeroCentsOrEmptyString(
        policyInfo.remaining_payment_amount
    )}</strong>. Please make your payment by clicking below.`;

const dueCopy = (policyInfo: CustomerPolicyInfo) =>
    `You have an outstanding balance of <strong>${formatAsDollarsWithoutZeroCentsOrEmptyString(
        policyInfo.remaining_payment_amount
    )}</strong>. ` + `To keep your policy active, please make your payment by clicking below.`;

const paymentRequiredCopy = (policyInfo: CustomerPolicyInfo) =>
    `You have an outstanding balance of <strong>${formatAsDollarsWithoutZeroCentsOrEmptyString(
        policyInfo.remaining_payment_amount
    )}</strong> past due. ` + `To keep your policy active, please make your payment by clicking below.`;

export const paymentStatusToActionTextMap = {
    paid: 'Everything looks good - you are all paid up.',
    payment_pending: {
        escrow: {
            copy: (policyInfo: CustomerPolicyInfo) =>
                `We're waiting for payment from your escrow account, which is due on ${
                    policyInfo.payment_due_date
                }.<br> ` + `<strong">Nothing to worry about.</strong>`
        },
        ach: {
            copy: (policyInfo: CustomerPolicyInfo) =>
                `We will charge your bank account on ${policyInfo.payment_due_date}. Nothing else is needed.`
        },
        cc: {
            copy: (policyInfo: CustomerPolicyInfo) =>
                `We will charge your credit card on ${policyInfo.payment_due_date}. Nothing else is needed.`
        }
    },
    pending_20_and_30_days_after_effective_date: {
        escrow: {
            copy: (policyInfo: CustomerPolicyInfo) =>
                `We haven't received payment from your escrow account, which is due on ${
                    policyInfo.payment_due_date
                }.` +
                `To make sure it's paid on time, please contact your escrow company or call us at ${SUPPORT_NUMBER}.`,
            header: 'Waiting for payment'
        }
    },
    pending_cancellation_non_payment: {
        escrow: {
            copy: (policyInfo: CustomerPolicyInfo) =>
                `Your policy will be cancelled on ${(policyInfo.cancellation) ? policyInfo.cancellation.update_date : ''} if payment isn't received. ` +
                `Please call us at ${SUPPORT_NUMBER} so we can take steps to keep your policy intact.`,
            header: 'Your policy may be cancelled'
        },
        cc: {
            copy:
                'We tried charging your credit card several times and have not been successful. ' +
                "To prevent your policy from being cancelled, please update your card's information and pay now.",
            header: 'Your policy may be cancelled',
            ctaSref: getPaymentsSref
        },
        ach: {
            copy:
                "We tried debiting your bank account and weren't successful. " +
                "Please take a moment to update your account's information. We'll try again tomorrow.",
            header: 'Your policy may be cancelled',
            ctaSref: getPaymentsSref
        }
    },
    policy_cancelled_non_payment: {
        escrow: {
            copy:
                "Your policy was cancelled because we didn't receive your escrow payment. We can remedy this. " +
                `To reinstate your policy, please call us at ${SUPPORT_NUMBER}.`,
            header: 'Policy cancelled'
        },
        ach: {
            copy:
                "Your policy was cancelled because we didn't receive payment. We can remedy this. " +
                `To reinstate your policy, please call us at ${SUPPORT_NUMBER}.`,
            header: 'Policy cancelled'
        },
        cc: {
            copy:
                "Your policy was cancelled because we didn't receive payment. We can remedy this. " +
                `To reinstate your policy, please call us at ${SUPPORT_NUMBER}.`,
            header: 'Policy cancelled'
        }
    },
    pending_cancellation_other:
        'Based on the cancellation date of your policy, you may be receiving a prorated refund.',
    policy_cancelled_other: "Your policy is cancelled. We're sorry to lose you as a member of the Hippo family.",
    cancelled_with_balance: {
        escrow: {
            copy: outstandingBalanceCopy,
            header: 'Outstanding balance',
            ctaSref: getPaymentsSrefWithPayNow
        },
        cc: {
            copy: outstandingBalanceCopy,
            header: 'Outstanding balance',
            ctaSref: getPaymentsSrefWithPayNow
        },
        ach: {
            copy: outstandingBalanceCopy,
            header: 'Outstanding balance',
            ctaSref: getPaymentsSrefWithPayNow
        }
    },
    outstanding_balance: {
        escrow: {
            copy: dueCopy,
            header: 'Payment due',
            ctaSref: getPaymentsSrefWithPayNow
        },
        ach: {
            copy: dueCopy,
            header: 'Payment due',
            ctaSref: getPaymentsSrefWithPayNow
        },
        cc: {
            copy: dueCopy,
            header: 'Payment due',
            ctaSref: getPaymentsSrefWithPayNow
        }
    },
    payment_failed: {
        cc: {
            copy:
                `We tried charging your credit card and weren't successful. Please take a moment to ` +
                "update your card's information. We'll try again tomorrow.",
            header: 'Payment unsuccessful',
            ctaSref: getPaymentsSref
        }
    },
    upcoming_payment: {
        cc: (policyInfo: CustomerPolicyInfo) =>
            `You have an upcoming payment on ${
                policyInfo.next_unpaid_payment_due_date
            } for <strong>${formatAsDollarsWithoutZeroCentsOrEmptyString(policyInfo.payment_amount)}</strong>.` +
            `You will be charged automatically or you can pay now by clicking below.`,
        ach: (policyInfo: CustomerPolicyInfo) =>
            `You have an upcoming payment on ${
                policyInfo.next_unpaid_payment_due_date
            } for <strong>${formatAsDollarsWithoutZeroCentsOrEmptyString(policyInfo.payment_amount)}</strong>.` +
            `Your bank account will be debited automatically or you can pay now by clicking below.`
    },
    payment_required: {
        cc: {
            copy: paymentRequiredCopy,
            header: 'Payment past due',
            ctaSref: getPaymentsSrefWithPayNow
        },
        ach: {
            copy: paymentRequiredCopy,
            header: 'Payment past due',
            ctaSref: getPaymentsSrefWithPayNow
        }
    },
    delayed: (policyInfo: CustomerPolicyInfo) =>
        'You will be charged automatically, or you can make a one time payment for the amount of ' +
        `<strong>${formatAsDollarsWithoutZeroCentsOrEmptyString(policyInfo.payment_amount)}</strong> by clicking below.`
};

function metShowEscrowCta(status: DERIVED_PAYMENT_STATUS, type: string) {
    const shared =
        status === DERIVED_PAYMENT_STATUS.PENDING_CANCELLATION_NON_PAYMENT ||
        status === DERIVED_PAYMENT_STATUS.CANCELLED_WITH_BALANCE ||
        status === DERIVED_PAYMENT_STATUS.OUTSTANDING_BALANCE;
    if (type === 'sidebar' || type === 'policyList') {
        return (
            shared ||
            status === DERIVED_PAYMENT_STATUS.PENDING_20_AND_30_DAYS_AFTER_EFFECTIVE_DATE ||
            status === DERIVED_PAYMENT_STATUS.POLICY_CANCELLED_NON_PAYMENT
        );
    }
    return shared;
}

function metShowCreditCardCta(status: DERIVED_PAYMENT_STATUS, type: string) {
    const shared =
        status === DERIVED_PAYMENT_STATUS.PAYMENT_FAILED ||
        status === DERIVED_PAYMENT_STATUS.PENDING_CANCELLATION_NON_PAYMENT ||
        status === DERIVED_PAYMENT_STATUS.OUTSTANDING_BALANCE ||
        status === DERIVED_PAYMENT_STATUS.PAYMENT_REQUIRED;
    if (type === 'sidebar' || type === 'policyList') {
        return shared || status === DERIVED_PAYMENT_STATUS.POLICY_CANCELLED_NON_PAYMENT;
    }
    return shared;
}

function metShowAchCta(status: DERIVED_PAYMENT_STATUS, type: string) {
    const shared =
        status === DERIVED_PAYMENT_STATUS.PENDING_CANCELLATION_NON_PAYMENT ||
        status === DERIVED_PAYMENT_STATUS.OUTSTANDING_BALANCE ||
        status === DERIVED_PAYMENT_STATUS.PAYMENT_REQUIRED;
    if (type === 'sidebar' || type === 'policyList') {
        return shared || status === DERIVED_PAYMENT_STATUS.POLICY_CANCELLED_NON_PAYMENT;
    }
    return shared;
}

export function shouldShowCta(policyInfo: CustomerPolicyInfo, type: string) {
    const derivedStatus = getDerivedPaymentStatus(policyInfo);
    if (!derivedStatus) {
        return false;
    }
    return (
        (policyInfo.checkout_data.payment_method === CUSTOMER_PAYMENT_METHOD.ESCROW &&
            metShowEscrowCta(derivedStatus, type)) ||
        (policyInfo.checkout_data.payment_method === CUSTOMER_PAYMENT_METHOD.CC &&
            metShowCreditCardCta(derivedStatus, type)) ||
        (policyInfo.checkout_data.payment_method === CUSTOMER_PAYMENT_METHOD.ACH && metShowAchCta(derivedStatus, type))
    );
}

const module = angular.module('customerPortal.utilities.paymentsHelper', []);

module.value('getDerivedPaymentStatus', getDerivedPaymentStatus);
module.value('shouldShowCta', shouldShowCta);
